import * as T from 'three'
import {createOakSoffitMaterial} from './oak-soffit-material'
import type { BasicUpgrades } from '../../../../shared/basicUpgrades'
import { bunkJoinery } from '../design/configurator/room/bunk-joinery'
import { bunkProfile, bunkGuardrailProfile } from '../design/configurator/room/bunk-profile'
import type { Bed } from '../design/configurator/room/beds'
/** Geometry is in local inches, converted to the planner's feet. */
export function addPhotoUpgrades(root:T.Group,bed:Bed,u:BasicUpgrades,ceiling:number) {
 const j=bunkJoinery(bed), w=bed.width,d=bed.depth, top=Math.max(84,ceiling)
 const {upperY}=bunkProfile(bed.height),guard=bunkGuardrailProfile(bed.height,2)
 const paint=new T.MeshStandardMaterial({color:0xf6f4ec,roughness:.65})
 function box(name:string,x:number,y:number,z:number,a:number,b:number,c:number,material:T.Material|T.Material[]=paint){const m=new T.Mesh(new T.BoxGeometry(a/12,b/12,c/12),material);m.name=name;m.position.set((x+a/2-w/2)/12,(y+b/2)/12,(z+c/2-d/2)/12);root.add(m);return m}
 // createBunkModel owns the floor-flush base, including the drawer plinth.
 // Do not overlay duplicate panels here: coplanar faces flicker in the photo preview.
 if(u.towerSoffit){
  // Start at the actual guard-post height; the top of every post meets the ceiling.
  for(const [i,x] of [j.upper.x,j.upper.x+j.upper.width-.75].entries())for(const [k,z] of [0,j.frameLength-3.5].entries())box(`tower-post-side-${i}-${k}`,x,guard.top,z,.75,top-guard.top,3.5)
  for(const [i,x] of [j.upper.x,j.upper.x+j.upper.width-3.5].entries())for(const [k,z] of [0,j.frameLength-.75].entries())box(`tower-post-end-${i}-${k}`,x,guard.top,z,3.5,top-guard.top,.75)
  box('ceiling-soffit',j.upper.x,top-6,0,j.upper.width,6,j.frameLength,[paint,paint,paint,createOakSoffitMaterial(),paint,paint])
 }
 for(const [key,y] of [['puckUpper',u.towerSoffit?top-6:top],['puckLower',upperY-.75]] as const){
  if(!u[key])continue
  // Four fixtures in a balanced 2 × 2 grid, inset one quarter from each edge.
  for(const across of [.25,.75])for(const along of [.25,.75]){
  const x=(j.upper.x+j.upper.width*across-w/2)/12,z=(j.frameLength*along-d/2)/12
  const rim=new T.Mesh(new T.CylinderGeometry(2/12,2/12,.65/12,32),new T.MeshStandardMaterial({color:0x69665d,roughness:.4,metalness:.35}));rim.name=`${key}-rim`;rim.position.set(x,(y-.325)/12,z);root.add(rim)
  // A distinct inset lens stays visible instead of washing the entire underside yellow.
  const lens=new T.Mesh(new T.CircleGeometry(1.65/12,32),new T.MeshBasicMaterial({color:0xffe5ac,side:T.FrontSide}));lens.name=key;lens.rotation.x=Math.PI/2;lens.position.set(x,(y-.7)/12,z);root.add(lens)
  const light=new T.SpotLight(0xffddb0,.55,8,Math.PI/3,.65,2);light.name=`${key}-light`;light.position.set(x,(y-.85)/12,z);
  const target=new T.Object3D();target.name=`${key}-target`;target.position.set(x,(y-24)/12,z);light.target=target;root.add(target,light)
  }
 }
 if(u.stairs){
  root.traverse(o=>{if(o.name.startsWith('ladder-')||/^guard-\d-foot-/.test(o.name)||o.name.startsWith('entry-support-'))o.visible=false})
  // Rear landing rail shares the long rear rail's outside plane. The 24-inch
  // flight runs across the foot (X), perpendicular to the bunk's length (Z).
  const rear=j.upper.x+.75,entryStart=rear+.75,entryEnd=entryStart+21,foot=j.frameLength
  const width=24,run=12,risers=Math.max(2,Math.round(upperY/12)),rise=upperY/risers
  box('stair-landing',entryStart,0,foot,21,upperY,width)
  box('stair-threshold',entryStart,upperY-.75,foot-1.5,21,.75,1.5)
  for(let i=0;i<risers-1;i++){
   const height=upperY-(i+1)*rise,x=entryEnd+i*run
   box(`stair-${i}`,x,0,foot,run,height,width)
   box(`stair-tread-${i}`,x,height-.75,foot,run,.75,width)
   if(u.stairDrawers){box(`stair-drawer-${i}`,x+run,height-rise+1,foot+1,.3,rise-2,width-2);box(`drawer-pull-${i}`,x+run+.3,height-rise/2,foot+width/2-2,.5,.5,4,new T.MeshStandardMaterial({color:0xb69950}))}
  }
  const inside=j.pocket(j.upper)
  for(let i=0;i<2;i++){
   const y=guard.base+i*(guard.face+guard.gap)
   box(`stair-landing-back-${i}`,rear,y,foot,.75,guard.face,width)
   box(`stair-landing-outside-${i}`,rear,y,foot+width-.75,entryEnd-rear,guard.face,.75)
   if(inside.x+inside.width>entryEnd)box(`stair-foot-return-${i}`,entryEnd,y,foot-1.5,inside.x+inside.width-entryEnd,guard.face,.75)
  }
  for(const x of [rear,entryEnd-.75])box('stair-landing-post',x,upperY,foot+width-.75,.75,guard.top-upperY,.75)
  box('stair-entry-post',entryEnd,upperY,foot-.75,.75,guard.top-upperY,.75)
  // No sloped handrails or flight balusters in the Basic package.
 }
}

/** Change only the extensions, soffit and upper light; never rescale the bunk. */
export function setPhotoCeiling(root:T.Object3D,height:number){
 if(!Number.isFinite(height))return
 const top=Math.max(80,Math.min(2400,height))
 root.traverse(o=>{
  if(!(o instanceof T.Mesh)&&!(o instanceof T.Light)&&!o.name.endsWith('-target'))return
  const isPost=o.name.startsWith('tower-post-'),isSoffit=o.name==='ceiling-soffit',isUpper=o.name.startsWith('puckUpper')
  if(!isPost&&!isSoffit&&!isUpper)return
  if(!o.userData.ceilingOriginal){
   const h=o instanceof T.Mesh?(o.geometry as T.BoxGeometry).parameters.height??0:0
   o.userData.ceilingOriginal={y:o.position.y,h,top:root.userData.ceilingInches??96}
  }
  const original=o.userData.ceilingOriginal
  if(isPost){const base=original.y-original.h/2,height=Math.max(.01,top/12-base);o.scale.y=height/original.h;o.position.y=base+height/2}
  else o.position.y=original.y+(top-original.top)/12
 })
}


